import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MachineClientRequest, MachineClientService, MachineClientTableResponsePaginatedList, RolesService } from '@src/app/shared/backend/clients';
import { FilterOperator } from '@src/app/shared/enums/FilterOperator.enum';
import { ComboData } from '@src/app/shared/components/comboData/comboData';
import { BaseListComponent } from '@src/app/shared/components/base-list/base-list.component';
import { TableSettingService } from '@src/app/shared/services/table.setting.service';
import { firstValueFrom } from 'rxjs';

@Component({
    selector: 'app-machine-client-settings',
    templateUrl: './machine-client-settings.component.html',
    styleUrls: ['./machine-client-settings.component.scss'],
    host: {
        class: 'app-machine-client-settings'
    },
    standalone: false
})
export class MachineClientSettingsComponent extends BaseListComponent implements OnInit {

  showCreateMachineClientModal = false;
  showViewMachineClientModal = false;

  showDeleteMachineClientModal = false;
  selectedMachineClientName: string = '';

  selectedMachineClientId: string = '';
  secretKey: string = '';
  showSecretKey = false;
  secretKeyCopied = false;

  filterOperator = FilterOperator;

  combosData: { [key: string]: ComboData } = {
    roles: new ComboData(async (filter) => await firstValueFrom(this.rolesService.apiRolesFilterComboPost(filter)))
  };

  public machineClientForm: FormGroup = this.fb.group({
    name: [null, [Validators.required]],
    roleId: [[], [Validators.required]]
  });

  public updateMachineClientForm: FormGroup = this.fb.group({
    name: [null, [Validators.required]],
    roleId: [[], [Validators.required]]
  });

  constructor(
    public override tableSettingService: TableSettingService,
    private fb: FormBuilder,
    private machineClientService: MachineClientService,
    private rolesService: RolesService
  ) {
    super();
  }

  ngOnInit() {
    this.init();
  }

  async init(): Promise<void> {
    this.reset();
    await this.tableSettingService.init();
    await this.initCombosData();
    await this.get();
  }

  private async initCombosData(): Promise<void> {
    this.combosData['roles'].additionalFilters = [
      { propertyName: 'isActive', operator: FilterOperator.EQUALS, values: ['true'] }
    ];

    await this.combosData['roles'].getData(null, 1);
  }

  override async get() {
    await super.setCache(TableSettingService.MachineClientsTableName);

    this.filter.pageNumber = this.pageNumber;
    this.filter.pageSize = this.pageSize;

    this.page = await firstValueFrom(this.machineClientService.apiMachineClientFilterTablePost(this.filter));
    this.data = this.page.results ?? [];
  }

  get paginatorPageNumber(): number {
    return this.pageNumber; // baseListComponent pageNumber is 1-based
  }


  async onPaginatorChange(event: { first: number; rows: number; page: number }): Promise<void> {
    this.pageNumber = event.page ?? 1;
    this.pageSize = event.rows ?? this.pageSize;

    await this.get();
  }

  async onLazyLoadComboData(comboType: 'roles', event: any) {
    const search = event?.search?.trim() || null;
    const activePage = Math.max(1, (event?.pageIndex ?? 1));

    await this.combosData[comboType].getData(search, activePage);
  }

  getComboTotalPages(comboType: 'roles'): number {
    const totalRecords = this.combosData[comboType]?.totalRecords ?? 0;
    const pageSize = Number(this.combosData[comboType]?.rowsPerPageOption) || 5;

    return Math.max(1, Math.ceil(totalRecords / pageSize));
  }

  reset() {
    this.machineClientForm.reset({
      name: null,
      roleId: []
    });

    this.updateMachineClientForm.reset({
      name: null,
      roleId: []
    });

    this.secretKey = '';
    this.showSecretKey = false;
    this.secretKeyCopied = false;
  }

  displayCreateMachineClientModalbox() {
    this.showCreateMachineClientModal = true;
  }

  cancelCreateMachineClient() {
    this.showCreateMachineClientModal = false;
    this.reset();
  }

  async createMachineClient() {
    const data: MachineClientRequest = {
      name: this.machineClientForm.controls['name'].value,
      roleId: this.machineClientForm.controls['roleId'].value?.[0]
    };

    await firstValueFrom(this.machineClientService.apiMachineClientCreatePost(data));

    this.showCreateMachineClientModal = false;
    this.reset();
    await this.get();
  }

  async onViewMachineClient(event: MouseEvent, machineClient: any) {
    event.preventDefault();

    this.selectedMachineClientId = machineClient.clientId;

    const [data, secretResponse] = await Promise.all([
      firstValueFrom(this.machineClientService.apiMachineClientIdGet(machineClient.id)),
      firstValueFrom(this.machineClientService.apiMachineClientIdSecretGet(machineClient.id))
    ]);

    this.secretKey = secretResponse?.secret ?? '';

    const roleId = data?.user?.role?.id ?? null;
    const role = data?.user?.role ?? null;

    if (role?.id) {
      const exists = this.combosData['roles'].list.some((item: any) => String(item.id) === String(role.id));

      if (!exists) {
        this.combosData['roles'].list = [
          { id: role.id, text: role.name },
          ...this.combosData['roles'].list
        ];
      }
    }

    this.updateMachineClientForm.patchValue({
      name: data.name,
      roleId: roleId ? [roleId] : []
    });

    this.showViewMachineClientModal = true;
  }

  async saveMachineClient() {
    const data: MachineClientRequest = {
      name: this.updateMachineClientForm.controls['name'].value,
      roleId: this.updateMachineClientForm.controls['roleId'].value?.[0]
    };

    await firstValueFrom(this.machineClientService.apiMachineClientUpdateIdPut(this.selectedMachineClientId, data));

    this.showViewMachineClientModal = false;
    this.reset();
    this.selectedMachineClientId = '';

    await this.get();
  }

  cancelViewMachineClient() {
    this.showViewMachineClientModal = false;
    this.reset();
    this.selectedMachineClientId = '';
  }

  onRemoveMachineClient(event: MouseEvent, machineClient: any) {
    event.preventDefault();

    this.selectedMachineClientId = machineClient.id;
    this.selectedMachineClientName = machineClient.name;
    this.showDeleteMachineClientModal = true;
  }

  async onRevokeMachineClient(event: MouseEvent, machineClientId: string) {
    event.preventDefault();

    await firstValueFrom(this.machineClientService.apiMachineClientRevokeIdPut(machineClientId));

    await this.get();
  }

  async onReactivateMachineClient(event: MouseEvent, machineClientId: string) {
    event.preventDefault();

    await firstValueFrom(this.machineClientService.apiMachineClientReactivateIdPut(machineClientId));

    await this.get();
  }

  cancelDeleteMachineClient() {
    this.showDeleteMachineClientModal = false;
    this.selectedMachineClientId = '';
    this.selectedMachineClientName = '';
  }

  async deleteMachineClient() {
    await firstValueFrom(this.machineClientService.apiMachineClientDeleteIdDelete(this.selectedMachineClientId));

    this.showDeleteMachineClientModal = false;
    this.reset();
    this.selectedMachineClientId = '';
    this.selectedMachineClientName = '';

    await this.get();
  }

  toggleSecretKeyVisibility(): void {
    this.showSecretKey = !this.showSecretKey;
  }

  async copySecretKey(): Promise<void> {
    if (!this.secretKey) {
      return;
    }

    await navigator.clipboard.writeText(this.secretKey);

    this.secretKeyCopied = true;

    setTimeout(() => {
      this.secretKeyCopied = false;
    }, 2000);
  }
}
